import { useEffect, useState } from 'react';
import { getPolicyDetail, savePolicyAdmin } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, Save, Loader2, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { uploadFile } from 'zite-file-upload-sdk';

interface Props {
  policyId?: string;
  categories: { id: string; name: string }[];
  onBack: () => void;
  onSaved: (id: string) => void;
}

const DEPARTMENTS = ['All', 'Front of House', 'Back of House', 'Kitchen', 'Management'];
const ROLES = ['All Staff', 'Trainee', 'Staff', 'Supervisor', 'Manager', 'Admin'];
const STATUSES = ['Draft', 'Under Review', 'Approved'];
const TAG_PRESETS = ['Induction', 'Mandatory', 'Refresher', 'Annual Review', 'Health & Safety', 'HR', 'Licensing', 'Food Safety'];

export default function PolicyEditorView({ policyId, categories, onBack, onSaved }: Props) {
  const [loading, setLoading] = useState(!!policyId);
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState<{ id: string; name: string }[]>([]);
  const [uploadingSource, setUploadingSource] = useState(false);
  const [newTag, setNewTag] = useState('');

  const [form, setForm] = useState({
    policyName: '', policyNumber: '', summary: '', policyContent: '', body: '',
    version: '1.0', lifecycleStatus: 'Draft',
    reviewDate: '', effectiveDate: '', acknowledgementDeadline: '',
    requiresAcknowledgement: false, isMandatory: false,
    declarationWording: 'I confirm that I have read, understood, and agree to comply with this policy.',
    deadlineRuleDays: 14, reminderIntervalDays: 7,
    primaryCategoryId: '', additionalCategoryIds: [] as string[],
    policyTags: [] as string[],
    applicableDepartments: [] as string[], applicableRoles: [] as string[],
    ownerId: '', approverId: '',
    changeSummary: '', sourceProvenance: '',
    sourceDocumentUrl: '', attachmentUrl: '',
  });

  useEffect(() => {
    if (!policyId) { setLoading(false); return; }
    getPolicyDetail({ policyId }).then(d => {
      const p = d.policy;
      setForm({
        policyName: p.policyName || '', policyNumber: p.policyNumber || '',
        summary: p.summary || '', policyContent: p.policyContent || '', body: p.body || '',
        version: p.version || '1.0', lifecycleStatus: p.lifecycleStatus || 'Draft',
        reviewDate: p.reviewDate || '', effectiveDate: p.effectiveDate || '',
        acknowledgementDeadline: p.acknowledgementDeadline || '',
        requiresAcknowledgement: p.requiresAcknowledgement || false,
        isMandatory: p.isMandatory || false,
        declarationWording: p.declarationWording || 'I confirm that I have read, understood, and agree to comply with this policy.',
        deadlineRuleDays: p.deadlineRuleDays || 14, reminderIntervalDays: p.reminderIntervalDays || 7,
        primaryCategoryId: p.primaryCategoryId || '',
        additionalCategoryIds: p.additionalCategoryIds || [],
        policyTags: p.policyTags || [],
        applicableDepartments: p.applicableDepartments || [],
        applicableRoles: p.applicableRoles || [],
        ownerId: p.ownerId || '', approverId: p.approverId || '',
        changeSummary: p.changeSummary || '', sourceProvenance: p.sourceProvenance || '',
        sourceDocumentUrl: p.sourceDocumentUrl || '', attachmentUrl: p.attachmentUrl || '',
      });
      setStaffList(d.staffList);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [policyId]);

  const update = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.policyName.trim()) { toast.error('Policy name is required'); return; }
    setSaving(true);
    try {
      const res = await savePolicyAdmin({ id: policyId, ...form });
      toast.success(res.created ? 'Policy created' : 'Policy saved');
      onSaved(res.id);
    } catch (e: any) { toast.error(e.message || 'Save failed'); }
    setSaving(false);
  };

  const handleSourceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSource(true);
    try {
      const { fileUrl: url } = await uploadFile({ data: file, filename: file.name });
      update('sourceDocumentUrl', url);
      toast.success('Source document uploaded');
    } catch { toast.error('Upload failed'); }
    setUploadingSource(false);
  };

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !form.policyTags.includes(trimmed)) {
      update('policyTags', [...form.policyTags, trimmed]);
    }
    setNewTag('');
  };

  const removeTag = (tag: string) => update('policyTags', form.policyTags.filter(t => t !== tag));

  const toggleAdditionalCategory = (catId: string) => {
    if (form.additionalCategoryIds.includes(catId)) {
      update('additionalCategoryIds', form.additionalCategoryIds.filter(c => c !== catId));
    } else {
      update('additionalCategoryIds', [...form.additionalCategoryIds, catId]);
    }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>;

  const isPublished = form.lifecycleStatus === 'Published';
  const availableAdditionalCats = categories.filter(c => c.id !== form.primaryCategoryId);

  return (
    <div className="max-w-3xl">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
        <ChevronLeft className="w-4 h-4 mr-1" />Back to Policies
      </Button>
      <h2 className="text-lg font-bold text-foreground mb-4">{policyId ? 'Edit Policy' : 'Create New Policy'}</h2>
      {isPublished && (
        <div className="bg-yellow-500/10 text-yellow-700 text-sm rounded-lg p-3 mb-4 flex items-start gap-2">
          <span className="font-semibold">Published policy:</span> Content changes will require a new version. Only metadata can be updated here.
        </div>
      )}

      <div className="space-y-6">
        <Section title="Identity">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Policy Name *"><Input value={form.policyName} onChange={e => update('policyName', e.target.value)} /></Field>
            <Field label="Policy Number"><Input value={form.policyNumber} onChange={e => update('policyNumber', e.target.value)} placeholder="e.g. POL-001" /></Field>
          </div>
          <Field label="Summary / Purpose"><Textarea value={form.summary} onChange={e => update('summary', e.target.value)} rows={2} placeholder="Brief purpose statement" /></Field>
        </Section>

        <Section title="Content">
          <Field label="Policy Content">
            <Textarea value={form.policyContent || form.body} onChange={e => update('policyContent', e.target.value)} rows={10} placeholder="Full policy text…" disabled={isPublished} />
          </Field>
          {isPublished && <p className="text-xs text-muted-foreground">To change content, create a new draft version from the policy detail view.</p>}
        </Section>

        <Section title="Classification">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Primary Category">
              <Select value={form.primaryCategoryId} onValueChange={v => update('primaryCategoryId', v)}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.lifecycleStatus} onValueChange={v => update('lifecycleStatus', v)} disabled={isPublished}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          {/* Additional Categories */}
          <Field label="Additional Categories">
            <div className="flex flex-wrap gap-2 mb-2">
              {form.additionalCategoryIds.map(catId => {
                const cat = categories.find(c => c.id === catId);
                return cat ? (
                  <Badge key={catId} variant="secondary" className="flex items-center gap-1 text-xs">
                    {cat.name}
                    <button onClick={() => toggleAdditionalCategory(catId)} className="ml-0.5 hover:text-destructive"><X className="w-3 h-3" /></button>
                  </Badge>
                ) : null;
              })}
            </div>
            {availableAdditionalCats.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {availableAdditionalCats.filter(c => !form.additionalCategoryIds.includes(c.id)).slice(0, 10).map(c => (
                  <button key={c.id} onClick={() => toggleAdditionalCategory(c.id)} className="text-xs px-2 py-1 rounded border border-dashed border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                    + {c.name}
                  </button>
                ))}
              </div>
            )}
          </Field>

          {/* Tags */}
          <Field label="Tags">
            <div className="flex flex-wrap gap-2 mb-2">
              {form.policyTags.map(tag => (
                <Badge key={tag} variant="outline" className="flex items-center gap-1 text-xs">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="ml-0.5 hover:text-destructive"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2 mb-2">
              <Input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Add a tag…" className="max-w-48 h-8 text-sm"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(newTag); } }} />
              <Button size="sm" variant="outline" className="h-8" onClick={() => addTag(newTag)} disabled={!newTag.trim()}>
                <Plus className="w-3 h-3 mr-1" />Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {TAG_PRESETS.filter(t => !form.policyTags.includes(t)).map(t => (
                <button key={t} onClick={() => addTag(t)} className="text-[11px] px-1.5 py-0.5 rounded border border-dashed border-border text-muted-foreground hover:bg-muted">
                  + {t}
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Applicable Departments">
              <div className="flex flex-wrap gap-2">
                {DEPARTMENTS.map(d => (
                  <label key={d} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={form.applicableDepartments.includes(d)} onChange={e => {
                      update('applicableDepartments', e.target.checked ? [...form.applicableDepartments, d] : form.applicableDepartments.filter(x => x !== d));
                    }} className="rounded" />{d}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Applicable Roles">
              <div className="flex flex-wrap gap-2">
                {ROLES.map(r => (
                  <label key={r} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={form.applicableRoles.includes(r)} onChange={e => {
                      update('applicableRoles', e.target.checked ? [...form.applicableRoles, r] : form.applicableRoles.filter(x => x !== r));
                    }} className="rounded" />{r}
                  </label>
                ))}
              </div>
            </Field>
          </div>
        </Section>

        <Section title="Versioning & Dates">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Version"><Input value={form.version} onChange={e => update('version', e.target.value)} disabled={isPublished} /></Field>
            <Field label="Effective Date"><Input type="date" value={form.effectiveDate} onChange={e => update('effectiveDate', e.target.value)} /></Field>
            <Field label="Review Date"><Input type="date" value={form.reviewDate} onChange={e => update('reviewDate', e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Ownership">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Policy Owner">
              <Select value={form.ownerId} onValueChange={v => update('ownerId', v)}>
                <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>{staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Approver">
              <Select value={form.approverId} onValueChange={v => update('approverId', v)}>
                <SelectTrigger><SelectValue placeholder="Select approver" /></SelectTrigger>
                <SelectContent>{staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        <Section title="Acknowledgement">
          <div className="flex items-center gap-3 mb-3">
            <Switch checked={form.requiresAcknowledgement} onCheckedChange={v => update('requiresAcknowledgement', v)} />
            <Label>Requires employee acknowledgement</Label>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <Switch checked={form.isMandatory} onCheckedChange={v => update('isMandatory', v)} />
            <Label>Mandatory policy</Label>
          </div>
          {form.requiresAcknowledgement && (
            <>
              <Field label="Declaration Wording">
                <Textarea value={form.declarationWording} onChange={e => update('declarationWording', e.target.value)} rows={2} />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Deadline (days from assignment)"><Input type="number" value={form.deadlineRuleDays} onChange={e => update('deadlineRuleDays', Number(e.target.value))} /></Field>
                <Field label="Reminder interval (days)"><Input type="number" value={form.reminderIntervalDays} onChange={e => update('reminderIntervalDays', Number(e.target.value))} /></Field>
                <Field label="Acknowledgement Deadline"><Input type="date" value={form.acknowledgementDeadline} onChange={e => update('acknowledgementDeadline', e.target.value)} /></Field>
              </div>
            </>
          )}
        </Section>

        <Section title="Source & Provenance">
          <Field label="Source Document">
            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <Input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleSourceUpload} />
                <span className="text-sm text-primary hover:underline">{uploadingSource ? 'Uploading…' : form.sourceDocumentUrl ? 'Replace file' : 'Upload source document'}</span>
              </label>
              {form.sourceDocumentUrl && (
                <a href={form.sourceDocumentUrl} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground">View current file</a>
              )}
            </div>
          </Field>
          <Field label="Source Provenance"><Input value={form.sourceProvenance} onChange={e => update('sourceProvenance', e.target.value)} placeholder="e.g. Originally from HR handbook v3" /></Field>
          <Field label="Change Summary"><Textarea value={form.changeSummary} onChange={e => update('changeSummary', e.target.value)} rows={2} placeholder="What changed in this version" /></Field>
        </Section>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={onBack}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {policyId ? 'Save Changes' : 'Create Policy'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
